import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query'
import { isNil } from 'lodash'
import apiClient from 'enrollment-platform/api/enrollment-platform-client/api-client'

import {
  GetPaymentAssignmentRequestDto,
  GetPaymentAssignmentResponseDto,
} from './dto/get-payment-assignment.dto'
import { GetPaymentAssignmentDetailResponseDto } from './dto/get-payment-assignment-detail.dto'
import { GetPaymentAssignmentBenefitEventsResponseDto } from './dto/get-payment-assignment-benefit-events.dto'
import {
  GetPaymentAssignmentUsersRequestDto,
  GetPaymentAssignmentUsersResponseDto,
} from './dto/get-payment-assignment-users.dto'
import { GetPaymentCarrierListResponseDto } from './dto/get-payment-carrier-list.dto'
import {
  GetClientMetricsRequestDto,
  GetClientMetricsResponseDto,
  GetPaymentMetricsResponseDto,
  MetricsDateRangeRequestDto,
} from './dto/get-payment-assignment-metrics.dto'
import {
  AssignPaymentAssignmentsRequestDto,
  AssignPaymentAssignmentsResponseDto,
} from './dto/assign-payment-assignments.dto'
import {
  StatusUpdateDto,
  StatusUpdateResponseDto,
} from './dto/set-payment-assignment-status.dto'
import {
  BatchClearPaymentAssignmentRequestDto,
  BatchClearPaymentAssignmentResponseDto,
} from './dto/batch-clear-payment-assignment.dto'
import {
  BatchUpdatePaymentAssignmentStatusRequestDto,
  BatchUpdatePaymentAssignmentStatusResponseDto,
} from './dto/batch-update-payment-assignment-status.dto'
import type { TaskSummary } from './dto/get-payment-assignment-task-summary.dto'
import {
  CreatePaymentBotTaskRequestDto,
  BulkCreatePaymentBotTaskRequestDto,
} from './dto/create-payment-bot-task.dto'
import {
  BulkCancelPaymentBotTaskRequestDto,
  BulkCancelPaymentBotTasksResponseDto,
  CancelPaymentBotTaskByAssignmentRequestDto,
  CancelPaymentBotTaskResponseDto,
} from './dto/cancel-payment-bot-task.dto'
import {
  BatchAssignByBenefitIdRequestDto,
  BatchAssignByBenefitIdResponseDto,
} from './dto/batch-assign-by-benefit-id.dto'
import {
  PaymentAssignmentRole,
  normalizePaymentAssignmentRole,
} from '../components/PaymentAssignment/Utils/roles'
import { PaginationResponse } from 'enrollment-platform/types/pagination-response-dto'

export const getPaymentAssignment = async (
  request: GetPaymentAssignmentRequestDto
): Promise<GetPaymentAssignmentResponseDto> => {
  const response = await apiClient.get<GetPaymentAssignmentResponseDto>(
    '/payment-assignment',
    { params: request }
  )
  return response.data
}

export const getPaymentAssignmentDetail = async (
  id: number
): Promise<GetPaymentAssignmentDetailResponseDto> => {
  const response = await apiClient.get<GetPaymentAssignmentDetailResponseDto>(
    `/payment-assignment/detail/${id}`
  )
  return response.data
}

export const getPaymentAssignmentBenefitEvents = async (
  id: number
): Promise<GetPaymentAssignmentBenefitEventsResponseDto> => {
  const response =
    await apiClient.get<GetPaymentAssignmentBenefitEventsResponseDto>(
      `/payment-assignment/benefit-events/${id}`
    )
  return response.data
}

export const getPaymentAssignmentUsers = async (
  request?: GetPaymentAssignmentUsersRequestDto
): Promise<GetPaymentAssignmentUsersResponseDto> => {
  const params = new URLSearchParams()
  for (const role of request?.roles ?? []) {
    params.append('roles[]', role)
  }
  const response = await apiClient.get<GetPaymentAssignmentUsersResponseDto>(
    '/payment-assignment/users',
    params.toString() ? { params } : undefined
  )
  return response.data
}

export const getPaymentCarrierList =
  async (): Promise<GetPaymentCarrierListResponseDto> => {
    const response =
      await apiClient.get<GetPaymentCarrierListResponseDto>('/payment-carrier')
    return response.data
  }

export const getPaymentCarrierListNames = async (): Promise<string[]> => {
  const response = await apiClient.get<PaginationResponse<string>>(
    '/payment-carrier/names'
  )
  return response.data.data
}

export const getPaymentMetrics = async (
  request: MetricsDateRangeRequestDto
): Promise<GetPaymentMetricsResponseDto> => {
  const { fromDate, toDate, month, year } = request
  const params = new URLSearchParams({
    ...(fromDate ? { fromDate: fromDate.toISOString() } : {}),
    ...(toDate ? { toDate: toDate.toISOString() } : {}),
    month: String(month),
    year: String(year),
  })

  const response = await apiClient.get<GetPaymentMetricsResponseDto>(
    '/payment-assignment/payment-metrics',
    { params }
  )
  return response.data
}

export const getClientMetrics = async (
  request: GetClientMetricsRequestDto
): Promise<GetClientMetricsResponseDto> => {
  const { fromDate, toDate, month, year } = request
  const params = new URLSearchParams({
    ...(fromDate ? { fromDate: fromDate.toISOString() } : {}),
    ...(toDate ? { toDate: toDate.toISOString() } : {}),
    month: String(month),
    year: String(year),
  })

  for (const clientPublicKey of request.clientPublicKeys) {
    params.append('clientPublicKeys[]', clientPublicKey)
  }

  const response = await apiClient.get<GetClientMetricsResponseDto>(
    '/payment-assignment/client-metrics',
    { params }
  )
  return response.data
}

export const usePaymentMetricsQuery = (
  request: MetricsDateRangeRequestDto | null,
  options?: Omit<
    UseQueryOptions<
      GetPaymentMetricsResponseDto,
      Error,
      GetPaymentMetricsResponseDto
    >,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery<
    GetPaymentMetricsResponseDto,
    Error,
    GetPaymentMetricsResponseDto
  >({
    queryKey: [
      'payment-metrics',
      request?.fromDate?.toISOString(),
      request?.toDate?.toISOString(),
      request?.month,
      request?.year,
    ],
    queryFn: () =>
      request !== null
        ? getPaymentMetrics(request)
        : Promise.reject(new Error('No params')),
    enabled: !isNil(request),
    ...options,
  })
}

export const useClientMetricsQuery = (
  request: GetClientMetricsRequestDto | null,
  options?: Omit<
    UseQueryOptions<
      GetClientMetricsResponseDto,
      Error,
      GetClientMetricsResponseDto
    >,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery<
    GetClientMetricsResponseDto,
    Error,
    GetClientMetricsResponseDto
  >({
    queryKey: [
      'client-metrics',
      request?.fromDate?.toISOString(),
      request?.toDate?.toISOString(),
      request?.month,
      request?.year,
      ...(request?.clientPublicKeys ?? []),
    ],
    queryFn: () =>
      request !== null
        ? getClientMetrics(request)
        : Promise.reject(new Error('No params')),
    enabled: !isNil(request) && (request?.clientPublicKeys?.length ?? 0) > 0,
    ...options,
  })
}

export const assignPaymentAssignments = async (
  request: AssignPaymentAssignmentsRequestDto
): Promise<AssignPaymentAssignmentsResponseDto> => {
  const hasAssignee =
    Number.isFinite(request.assigneeUserId) || request.assigneeUserId === null
  const hasEscalator =
    Number.isFinite(request.escalatorUserId) || request.escalatorUserId === null
  const hasPriority = !isNil(request.priority)
  if (!hasAssignee && !hasEscalator && !hasPriority) {
    throw new Error(
      'assignPaymentAssignments requires at least one of assigneeUserId, escalatorUserId or priority'
    )
  }
  const response = await apiClient.post<AssignPaymentAssignmentsResponseDto>(
    '/payment-assignment/assign',
    request
  )
  return response.data
}

export const setPaymentAssignmentStatus = async (
  request: StatusUpdateDto
): Promise<StatusUpdateResponseDto> => {
  const response = await apiClient.post('/payment-assignment/status', request)
  return response.data
}

export const batchClearPaymentAssignmentStatus = async (
  request: BatchClearPaymentAssignmentRequestDto
): Promise<BatchClearPaymentAssignmentResponseDto> => {
  const response = await apiClient.post<BatchClearPaymentAssignmentResponseDto>(
    '/payment-assignment/batch-clear-status',
    request
  )
  return response.data
}

export const batchUpdatePaymentAssignmentStatus = async (
  request: BatchUpdatePaymentAssignmentStatusRequestDto
): Promise<BatchUpdatePaymentAssignmentStatusResponseDto> => {
  const response =
    await apiClient.post<BatchUpdatePaymentAssignmentStatusResponseDto>(
      '/payment-assignment/bulk-update-status-by-benefit-ids',
      request
    )
  return response.data
}

export const usePaymentAssignmentDetailQuery = (
  id: number | null,
  options?: Omit<
    UseQueryOptions<
      GetPaymentAssignmentDetailResponseDto,
      Error,
      GetPaymentAssignmentDetailResponseDto
    >,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery<
    GetPaymentAssignmentDetailResponseDto,
    Error,
    GetPaymentAssignmentDetailResponseDto
  >({
    queryKey: ['payment-assignment-detail', id],
    queryFn: () => getPaymentAssignmentDetail(id as number),
    enabled: !isNil(id),
    ...options,
  })
}

export const usePaymentAssignmentBenefitEventsQuery = (
  id: number | null,
  options?: Omit<
    UseQueryOptions<
      GetPaymentAssignmentBenefitEventsResponseDto,
      Error,
      GetPaymentAssignmentBenefitEventsResponseDto
    >,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery<
    GetPaymentAssignmentBenefitEventsResponseDto,
    Error,
    GetPaymentAssignmentBenefitEventsResponseDto
  >({
    queryKey: ['payment-assignment-benefit-events', id],
    queryFn: () => getPaymentAssignmentBenefitEvents(id as number),
    enabled: !isNil(id),
    ...options,
  })
}

export const usePaymentAssignmentUsersQuery = (
  request?: GetPaymentAssignmentUsersRequestDto,
  options?: Omit<
    UseQueryOptions<
      GetPaymentAssignmentUsersResponseDto,
      Error,
      GetPaymentAssignmentUsersResponseDto
    >,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery<
    GetPaymentAssignmentUsersResponseDto,
    Error,
    GetPaymentAssignmentUsersResponseDto
  >({
    queryKey: ['payment-assignment-users', request],
    queryFn: () => getPaymentAssignmentUsers(request),
    staleTime: 5 * 60 * 1000,
    retry: false,
    ...options,
  })
}

export const usePaymentCarrierListQuery = (
  options?: Omit<
    UseQueryOptions<
      GetPaymentCarrierListResponseDto,
      Error,
      GetPaymentCarrierListResponseDto
    >,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery<
    GetPaymentCarrierListResponseDto,
    Error,
    GetPaymentCarrierListResponseDto
  >({
    queryKey: ['payment-carrier-list'],
    queryFn: getPaymentCarrierList,
    staleTime: 10 * 60 * 1000,
    retry: false,
    ...options,
  })
}

export const usePaymentCarrierListNameQuery = (
  options?: Omit<
    UseQueryOptions<string[], Error, string[]>,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery<string[], Error, string[]>({
    queryKey: ['payment-carrier-list-names'],
    queryFn: getPaymentCarrierListNames,
    staleTime: 10 * 60 * 1000,
    retry: false,
    ...options,
  })
}

export const useAssignPaymentAssignmentsMutation = (
  options?: UseMutationOptions<
    AssignPaymentAssignmentsResponseDto,
    Error,
    AssignPaymentAssignmentsRequestDto
  >
) => {
  return useMutation<
    AssignPaymentAssignmentsResponseDto,
    Error,
    AssignPaymentAssignmentsRequestDto
  >({
    mutationFn: assignPaymentAssignments,
    ...options,
  })
}

export const useSetPaymentAssignmentStatusMutation = (
  options?: UseMutationOptions<unknown, Error, StatusUpdateDto>
) => {
  return useMutation<unknown, Error, StatusUpdateDto>({
    mutationFn: setPaymentAssignmentStatus,
    ...options,
  })
}

export const useBatchClearPaymentAssignmentStatusMutation = (
  options?: UseMutationOptions<
    BatchClearPaymentAssignmentResponseDto,
    Error,
    BatchClearPaymentAssignmentRequestDto
  >
) => {
  return useMutation<
    BatchClearPaymentAssignmentResponseDto,
    Error,
    BatchClearPaymentAssignmentRequestDto
  >({
    mutationFn: batchClearPaymentAssignmentStatus,
    ...options,
  })
}

export const useBatchUpdatePaymentAssignmentStatusMutation = (
  options?: UseMutationOptions<
    BatchUpdatePaymentAssignmentStatusResponseDto,
    Error,
    BatchUpdatePaymentAssignmentStatusRequestDto
  >
) => {
  return useMutation<
    BatchUpdatePaymentAssignmentStatusResponseDto,
    Error,
    BatchUpdatePaymentAssignmentStatusRequestDto
  >({
    mutationFn: batchUpdatePaymentAssignmentStatus,
    ...options,
  })
}

export const usePaymentAssignmentQuery = (
  request: GetPaymentAssignmentRequestDto,
  refreshKey?: number,
  options?: Omit<
    UseQueryOptions<
      GetPaymentAssignmentResponseDto,
      Error,
      GetPaymentAssignmentResponseDto
    >,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery<
    GetPaymentAssignmentResponseDto,
    Error,
    GetPaymentAssignmentResponseDto
  >({
    queryKey: ['payment-assignment', request, refreshKey ?? 0],
    queryFn: () => getPaymentAssignment(request),
    ...options,
  })
}

export const getPaymentAssignmentUserRole = async () => {
  try {
    const response = await apiClient.get<{ role?: string }>(
      '/payment-assignment/user-role'
    )
    return {
      role: normalizePaymentAssignmentRole(response.data?.role),
    }
  } catch {
    return { role: PaymentAssignmentRole.UNKNOWN }
  }
}

export const usePaymentAssignmentUserRoleQuery = (
  options?: Omit<
    UseQueryOptions<
      { role: PaymentAssignmentRole },
      Error,
      { role: PaymentAssignmentRole }
    >,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery<
    { role: PaymentAssignmentRole },
    Error,
    { role: PaymentAssignmentRole }
  >({
    queryKey: ['payment-assignment-user-role'],
    queryFn: getPaymentAssignmentUserRole,
    retry: false,
    ...options,
  })
}

export interface GetPaymentAssignmentTaskSummaryParams {
  month: number
  year: number
  testUserFilter?: string
  terminatedFilter?: string
}

export const getPaymentAssignmentTaskSummary = async (
  params: GetPaymentAssignmentTaskSummaryParams
): Promise<TaskSummary> => {
  const response = await apiClient.get<TaskSummary>(
    '/payment-assignment/task-summary',
    {
      params: {
        month: params.month,
        year: params.year,
        ...(params.testUserFilter
          ? { testUserFilter: params.testUserFilter }
          : {}),
        ...(params.terminatedFilter
          ? { terminatedFilter: params.terminatedFilter }
          : {}),
      },
    }
  )
  return response.data
}

export const usePaymentAssignmentTaskSummaryQuery = (
  params: GetPaymentAssignmentTaskSummaryParams | null,
  options?: Omit<
    UseQueryOptions<TaskSummary, Error, TaskSummary>,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery<TaskSummary, Error, TaskSummary>({
    queryKey: [
      'payment-assignment-task-summary',
      params?.month,
      params?.year,
      params?.testUserFilter,
      params?.terminatedFilter,
    ],
    queryFn: () =>
      params !== null
        ? getPaymentAssignmentTaskSummary(params)
        : Promise.reject(new Error('No params')),
    enabled:
      params !== null &&
      Number.isInteger(params.month) &&
      params.month >= 0 &&
      params.month <= 11 &&
      Number.isInteger(params.year) &&
      params.year > 0,
    ...options,
  })
}

export const createPaymentBotTask = async (
  body: CreatePaymentBotTaskRequestDto
) => {
  const response = await apiClient.post('/payment-bot/tasks/', body)
  return response.data
}

export const useCreatePaymentBotTaskMutation = (
  options?: UseMutationOptions<unknown, Error, CreatePaymentBotTaskRequestDto>
) => {
  const queryClient = useQueryClient()
  return useMutation<unknown, Error, CreatePaymentBotTaskRequestDto>({
    ...options,
    mutationFn: createPaymentBotTask,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ['payment-assignment'] })
      options?.onSuccess?.(data, variables, context)
    },
  })
}

export const bulkCreatePaymentBotTask = async (
  body: BulkCreatePaymentBotTaskRequestDto
) => {
  const response = await apiClient.post('/payment-bot/tasks/bulk', body)
  return response.data
}

export const useBulkCreatePaymentBotTaskMutation = (
  options?: UseMutationOptions<
    unknown,
    Error,
    BulkCreatePaymentBotTaskRequestDto
  >
) => {
  const queryClient = useQueryClient()
  return useMutation<unknown, Error, BulkCreatePaymentBotTaskRequestDto>({
    ...options,
    mutationFn: bulkCreatePaymentBotTask,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ['payment-assignment'] })
      options?.onSuccess?.(data, variables, context)
    },
  })
}

export const cancelPaymentBotTaskByPaymentAssignmentId = async (
  body: CancelPaymentBotTaskByAssignmentRequestDto
): Promise<CancelPaymentBotTaskResponseDto> => {
  const response = await apiClient.post<CancelPaymentBotTaskResponseDto>(
    '/payment-bot/tasks/cancel',
    body
  )
  return response.data
}

export const bulkCancelPaymentBotTasks = async (
  body: BulkCancelPaymentBotTaskRequestDto
): Promise<BulkCancelPaymentBotTasksResponseDto> => {
  const response = await apiClient.post<BulkCancelPaymentBotTasksResponseDto>(
    '/payment-bot/tasks/bulk-cancel',
    body
  )
  return response.data
}

const invalidatePaymentAssignmentQueries = (
  queryClient: ReturnType<typeof useQueryClient>
) => {
  void queryClient.invalidateQueries({ queryKey: ['payment-assignment'] })
  void queryClient.invalidateQueries({
    queryKey: ['payment-assignment-task-summary'],
  })
}

export const useCancelPaymentBotTaskMutation = (
  options?: UseMutationOptions<
    CancelPaymentBotTaskResponseDto,
    Error,
    CancelPaymentBotTaskByAssignmentRequestDto
  >
) => {
  const queryClient = useQueryClient()
  return useMutation<
    CancelPaymentBotTaskResponseDto,
    Error,
    CancelPaymentBotTaskByAssignmentRequestDto
  >({
    ...options,
    mutationFn: cancelPaymentBotTaskByPaymentAssignmentId,
    onSuccess: (data, variables, context) => {
      invalidatePaymentAssignmentQueries(queryClient)
      options?.onSuccess?.(data, variables, context)
    },
  })
}

export const useBulkCancelPaymentBotTaskMutation = (
  options?: UseMutationOptions<
    BulkCancelPaymentBotTasksResponseDto,
    Error,
    BulkCancelPaymentBotTaskRequestDto
  >
) => {
  const queryClient = useQueryClient()
  return useMutation<
    BulkCancelPaymentBotTasksResponseDto,
    Error,
    BulkCancelPaymentBotTaskRequestDto
  >({
    ...options,
    mutationFn: bulkCancelPaymentBotTasks,
    onSuccess: (data, variables, context) => {
      invalidatePaymentAssignmentQueries(queryClient)
      options?.onSuccess?.(data, variables, context)
    },
  })
}

export const batchAssignByBenefitId = async (
  request: BatchAssignByBenefitIdRequestDto
): Promise<BatchAssignByBenefitIdResponseDto> => {
  const response = await apiClient.post<BatchAssignByBenefitIdResponseDto>(
    '/payment-assignment/bulk-assign-by-benefit-id',
    request
  )
  return response.data
}
